import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  getPermissionControllers,
  hasCustomerPermission,
  getKycStatusValue,
  isKycVerified,
  isAccountApprovalLocked,
} from "../utils/permissionHelper";
import PermissionCustomerModelController from "../controller/model/PermissionCustomerModelController";
import backgroundImage from "../assets/images/Gemini_Generated_Image_el0b3yel0b3yel0b.png";
import brandLogoDark from "../utils/brandLogo";
import cashInIcon from "../assets/images/group-1000004874.png";
import cashOutIcon from "../assets/images/group-1000004874-1.png";
import historyIcon from "../assets/images/arrow-right-to-line.png";

const CreditCard = ({
  balance = "0.0",
  currency = "USD",
  isSmall = false,
  isLoadingData = false,
  user = null,
  onCashInNoPermission = null,
  onCashOutNoPermission = null,
  onHistoryNoPermission = null,
}) => {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  
  // Get permission controllers from user data
  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const {
    customerPerm,
    customerDevicePerm,
  } = permissions;

  // Check permissions for Cash In, Cash Out, and History
  const canCashIn = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_IN
  );
  const canCashOut = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_OUT
  );
  const kycStatusValue = getKycStatusValue(user);
  const approvalLocked = isAccountApprovalLocked(user);
  const userIsKycVerified = isKycVerified(user);
  const isAccountVerificationRequired =
    kycStatusValue === 1 || kycStatusValue === -3 || kycStatusValue === -2;
  const canCashOutAction =
    canCashOut &&
    !approvalLocked &&
    userIsKycVerified &&
    !isAccountVerificationRequired;
  const canCashInAction = canCashIn && !approvalLocked;
  const canViewHistory = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_HISTORY
  );
  const canViewHistoryAction = canViewHistory && !approvalLocked;
  // Get currency symbol from library
  const getCurrencySymbol = (curr) => {
    const symbol = getSymbolFromCurrency(curr);
    // Handle special cases
    if (curr === "LBP") {
      return "LL"; // Lebanese Pound uses "LL" prefix
    }
    return symbol || "$"; // Fallback to $ if symbol not found
  };

  // Format balance based on currency
  const formatBalance = (bal, curr) => {
    const parsed = typeof bal === "string" ? parseFloat(bal.replace(/,/g, "")) : Number(bal);
    const normalized = Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
    const symbol = getCurrencySymbol(curr);
    if (curr === "LBP") {
      return `${symbol} ${normalized}`;
    }
    // For most currencies, symbol comes before the amount
    return `${symbol}${normalized}`;
  };

  const currencySymbol = getCurrencySymbol(currency);

  if (isSmall) {
    // Small card variant for secondary currencies
    return (
      <div className={`credit-card-small ${currency.toLowerCase()}-card`}>
        <div className="dollar-icon-button-small">
          <span className="dollar-sign-small">{currencySymbol}</span>
        </div>
        <div className="balance-amount-small">
          {isLoadingData ? <span className="credit-card-data-shimmer credit-card-data-shimmer--small" /> : formatBalance(balance, currency)}
        </div>
        <div className="currency-text-small">
          {isLoadingData ? <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" /> : currency}
        </div>
      </div>
    );
  }

  return (
    <div className="credit-card-group">
      {/* Main Credit Card Rectangle */}
      <div 
        className="credit-card-rectangle"
        style={{
          '--bg-image': `url(${backgroundImage})`
        }}
      >
        {/* Your Balance Text */}
        <div className="your-balance-text">{t("your_balance")}</div>
        
        {/* Balance Amount */}
        <div className="balance-amount">
          {isLoadingData ? <span className="credit-card-data-shimmer credit-card-data-shimmer--balance" /> : formatBalance(balance, currency)}
        </div>
        
        {/* Currency Text */}
        <div className="usd-text">
          {isLoadingData ? <span className="credit-card-data-shimmer credit-card-data-shimmer--currency" /> : currency}
        </div>
        
        {/* Currency Symbol Icon Button */}
        <div className="dollar-icon-button">
          <span className="dollar-sign">{currencySymbol}</span>
        </div>
        
        {/* Logo */}
        <div className="credit-card-logo">
          <img
            src={brandLogoDark}
            alt="MFG Invest"
            className="credit-card-logo-img"
          />
        </div>
        
        {/* Divider Line */}
        <div className="credit-card-divider"></div>
      </div>
      
      {/* Cash In / Cash Out Frame */}
      <div className="cash-actions-frame">
        <div
          className={`cash-action-item ${!canCashInAction ? "cash-action-item--view-only" : ""}`}
          onClick={() => {
            if (canCashInAction) {
              navigate("/pages/cash-in");
              return;
            }
            if (typeof onCashInNoPermission === "function") {
              onCashInNoPermission();
            }
          }}
          title={!canCashInAction ? t("dont_have_permissions") : t("cashin")}
        >
          <div className="cash-action-icon cash-in-icon">
            <img src={cashInIcon} alt={t("cashin")} />
          </div>
          <div className="cash-action-text">{t("cashin")}</div>
        </div>
        <div
          className={`cash-action-item ${!canCashOutAction ? "cash-action-item--view-only" : ""}`}
          onClick={() => {
            if (canCashOutAction) {
              navigate("/pages/cash-out");
              return;
            }
            if (typeof onCashOutNoPermission === "function") {
              onCashOutNoPermission();
            }
          }}
          title={!canCashOutAction ? t("dont_have_permissions") : t("cashout")}
        >
          <div className="cash-action-icon cash-out-icon">
            <img src={cashOutIcon} alt={t("cashout")} />
          </div>
          <div className="cash-action-text">{t("cashout")}</div>
        </div>
        <div
          className={`cash-action-item ${!canViewHistoryAction ? "cash-action-item--view-only" : ""}`}
          onClick={() => {
            if (canViewHistoryAction) {
              navigate("/pages/transactions/history");
              return;
            }
            if (typeof onHistoryNoPermission === "function") {
              onHistoryNoPermission();
            }
          }}
          title={!canViewHistoryAction ? t("dont_have_permissions") : t("history")}
        >
          <div className="cash-action-icon history-icon">
            <img src={historyIcon} alt={t("history")} />
          </div>
          <div className="cash-action-text">{t("history")}</div>
        </div>
      </div>
    </div>
  );
};

export default CreditCard;
